import { fetchMyBuilds } from '@/api/builds';
import {
	createLandingLink,
	fetchLandingMetaPixels,
	fetchLandingSites,
	fetchLandingTemplates,
	fetchLandingUploadedBuilds,
} from '@/api/landings';
import { ROUTES } from '@/app/routes';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { IBuild } from '@/entities/build.entity';
import type {
	ILandingLinkCreated,
	ILandingMetaPixel,
	ILandingSite,
	ILandingTemplate,
	ILandingUploadedBuild,
	LandingDownloadTrigger,
} from '@/entities/landing.entity';
import { formatMetaPixelOptionLabel } from '@/entities/landing.entity';
import {
	isTelegramAuthLandingTemplate,
	isTelegramSite,
	pickDefaultSiteId,
} from '@/pages/landings/lib/landing-site-mode';
import { LayoutTemplateIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import LandingApkPicker from './landing-apk-picker';
import DownloadTriggerSelect from './download-trigger-select';

interface CreateLinkModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated: (link: ILandingLinkCreated) => void;
}

export default function CreateLinkModal({ open, onOpenChange, onCreated }: CreateLinkModalProps) {
	const navigate = useNavigate();
	const [sites, setSites] = useState<ILandingSite[]>([]);
	const [builds, setBuilds] = useState<IBuild[]>([]);
	const [uploadedBuilds, setUploadedBuilds] = useState<ILandingUploadedBuild[]>([]);
	const [pixels, setPixels] = useState<ILandingMetaPixel[]>([]);
	const [siteId, setSiteId] = useState('');
	const [buildId, setBuildId] = useState('');
	const [tgUrl, setTgUrl] = useState('');
	const [metaPixelId, setMetaPixelId] = useState('none');
	const [downloadTrigger, setDownloadTrigger] = useState<LandingDownloadTrigger>('cta');
	const [loadingMeta, setLoadingMeta] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const selectedSite = useMemo(
		() => sites.find(s => String(s.id) === siteId),
		[sites, siteId],
	);
	const [templates, setTemplates] = useState<ILandingTemplate[]>([]);
	const selectedTemplate = useMemo(
		() => templates.find(t => t.slug === selectedSite?.templateSlug),
		[templates, selectedSite?.templateSlug],
	);
	const telegramAuthLanding = isTelegramAuthLandingTemplate(
		selectedTemplate,
		selectedSite?.templateSlug ?? '',
	);
	const telegramMode = isTelegramSite(selectedSite);
	const apkBuildRequired = !telegramMode && !telegramAuthLanding && !buildId;

	useEffect(() => {
		if (!open) return;
		let cancelled = false;

		async function loadMeta() {
			setLoadingMeta(true);
			try {
				const [siteItems, buildItems, pixelItems, uploadedItems, templateItems] = await Promise.all([
					fetchLandingSites(),
					fetchMyBuilds(),
					fetchLandingMetaPixels(),
					fetchLandingUploadedBuilds().catch(() => [] as ILandingUploadedBuild[]),
					fetchLandingTemplates(),
				]);
				if (cancelled) return;
				setSites(siteItems);
				setTemplates(templateItems);
				const readyBuilds = buildItems.filter(b => b.hasApk && b.successBuilded);
				setBuilds(readyBuilds);
				setUploadedBuilds(uploadedItems);
				setPixels(pixelItems);
				const defaultSiteId = pickDefaultSiteId(siteItems, readyBuilds.length + uploadedItems.length);
				if (defaultSiteId) setSiteId(defaultSiteId);
				if (readyBuilds.length > 0) {
					setBuildId(readyBuilds[0].id);
				} else if (uploadedItems.length > 0) {
					setBuildId(uploadedItems[0].buildId);
				} else {
					setBuildId('');
				}
				setTgUrl('');
				setMetaPixelId('none');
			} finally {
				if (!cancelled) setLoadingMeta(false);
			}
		}

		void loadMeta();
		return () => {
			cancelled = true;
		};
	}, [open]);

	function handleOpenChange(next: boolean) {
		if (!next) {
			setSiteId('');
			setBuildId('');
			setTgUrl('');
			setMetaPixelId('none');
			setDownloadTrigger('cta');
			setUploadedBuilds([]);
		}
		onOpenChange(next);
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		const parsedSiteId = Number(siteId);
		if (!Number.isFinite(parsedSiteId)) {
			toast.error('Выберите сайт');
			return;
		}

		if (!telegramAuthLanding) {
			if (telegramMode) {
				const trimmedTgUrl = tgUrl.trim();
				if (!trimmedTgUrl) {
					toast.error('Укажите URL Telegram');
					return;
				}
			} else if (!buildId) {
				toast.error('Выберите сборку APK');
				return;
			}
		}

		setIsSaving(true);
		try {
			const parsedMetaPixelId = metaPixelId === 'none' ? undefined : Number(metaPixelId);
			const link = await createLandingLink({
				siteId: parsedSiteId,
				...(telegramAuthLanding
					? {}
					: telegramMode
						? { tgUrl: tgUrl.trim() }
						: { buildId, downloadTrigger }),
				...(parsedMetaPixelId !== undefined && Number.isFinite(parsedMetaPixelId)
					? { metaPixelId: parsedMetaPixelId }
					: {}),
			});
			toast.success('Ссылка создана');
			handleOpenChange(false);
			onCreated(link);
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className='sm:max-w-[440px]'>
				<DialogHeader>
					<DialogTitle>Создать ссылку</DialogTitle>
					<DialogDescription>
						{telegramAuthLanding
							? 'Ссылка для Telegram login лендинга (phone/code). APK не нужен.'
							: telegramMode
								? 'Ссылка для трекинга; клик по фото откроет указанный Telegram-канал.'
								: 'Ссылка привязывается к сайту и APK-сборке для трекинга трафика.'}
					</DialogDescription>
				</DialogHeader>
				{loadingMeta ? (
					<div className='flex flex-col gap-4'>
						<Skeleton className='h-10 w-full' />
						<Skeleton className='h-10 w-full' />
					</div>
				) : sites.length === 0 ? (
					<div className='flex flex-col items-center gap-3 py-4 text-center'>
						<div className='rounded-full bg-muted/60 p-3'>
							<LayoutTemplateIcon size={20} className='text-muted-foreground' />
						</div>
						<p className='text-sm text-muted-foreground max-w-[280px]'>Сначала создайте сайт на домене лендинга.</p>
						<Button
							type='button'
							variant='outline'
							size='sm'
							onClick={() => {
								handleOpenChange(false);
								navigate(ROUTES.landingsSites);
							}}
						>
							Создать сайт
						</Button>
					</div>
				) : (
					<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
						<div className='flex flex-col gap-1.5'>
							<Label>Сайт</Label>
							<Select value={siteId} onValueChange={setSiteId}>
								<SelectTrigger>
									<SelectValue placeholder='Выберите сайт' />
								</SelectTrigger>
								<SelectContent>
									{sites.map(s => (
										<SelectItem key={s.id} value={String(s.id)}>
											{s.fullHostname}
											{s.config?.mode === 'telegram' ? ' · Telegram' : ''}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{telegramAuthLanding ? (
							<p className='text-xs text-muted-foreground'>
								Достаточно сайта и Meta Pixel по желанию.
							</p>
						) : telegramMode ? (
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='landing-tg-url'>URL Telegram</Label>
								<InputGroup>
									<InputGroupInput
										id='landing-tg-url'
										placeholder='https://t.me/your_channel'
										value={tgUrl}
										onChange={e => setTgUrl(e.target.value)}
										disabled={isSaving}
									/>
								</InputGroup>
								<p className='text-xs text-muted-foreground'>t.me, telegram.me или tg://</p>
							</div>
						) : (
							<>
								<LandingApkPicker
									panelBuilds={builds}
									uploadedBuilds={uploadedBuilds}
									buildId={buildId}
									onBuildIdChange={setBuildId}
									onUploaded={uploaded => {
										setUploadedBuilds(prev =>
											prev.some(b => b.buildId === uploaded.buildId)
												? prev
												: [uploaded, ...prev],
										);
										setBuildId(uploaded.buildId);
									}}
									disabled={isSaving}
								/>
								<DownloadTriggerSelect
									value={downloadTrigger}
									onValueChange={setDownloadTrigger}
									disabled={isSaving}
								/>
							</>
						)}

						<div className='flex flex-col gap-1.5'>
							<Label>Meta Pixel</Label>
							<Select value={metaPixelId} onValueChange={setMetaPixelId}>
								<SelectTrigger>
									<SelectValue placeholder='Без пикселя' />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='none'>Без пикселя</SelectItem>
									{pixels.map(pixel => (
										<SelectItem key={pixel.id} value={String(pixel.id)}>
											{formatMetaPixelOptionLabel(pixel)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<DialogFooter className='flex-col sm:flex-col'>
							<div className='w-full'>
								<LoadingWrapper show={isSaving}>
									<Button
										type='submit'
										className='w-full'
										disabled={isSaving || apkBuildRequired}
									>
										Создать
									</Button>
								</LoadingWrapper>
							</div>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
